import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { SelectNoScrollCapture as Select } from 'components/Inputs/Select';
import { ValidatedDecimalNumberTextInput } from 'components/Inputs/TextInput/presets/DecimalNumber';
import { ELEMENT_TYPES } from '../constants';
import { useFlashErrorDetails } from 'pages/AutomationEngine/hooks/useFlashErrorDetails';
import { useOnScreen } from '../../llm-common/useOnScreen';
import { addDeleted } from 'pages/AutomationEngine/dialogs/common';
import { GroupedVariableSelectWithAdd } from '__pages/AutomationEngine/dialogs/VariableSelect/GroupedVariableSelectWithAdd';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';

const INPUT_WIDTH = 175;

const ALLOWED_VARIABLE_DATA_TYPES = {
  [AUTOMATION_VARIABLE_DATA_TYPES.number]: {
    single: {},
  },
};

const ElementInputWrapper = styled.div`
  width: ${INPUT_WIDTH}px;
`;

export const ElementInputs = ({
  element,
  onUpdateElement,
  fieldOptions,
  index,
}) => {
  const { t } = useTranslation();
  const componentRef = useRef(null);

  const { optionsForFields, optionsForVariables } = useMemo(() => {
    return fieldOptions.reduce(
      (prev, category) => {
        if (category.label !== t('Variables')) {
          prev.optionsForFields.push(category);
        } else {
          prev.optionsForVariables.push(category);
        }

        return prev;
      },
      { optionsForFields: [], optionsForVariables: [] }
    );
  }, [fieldOptions, t]);

  const { value } = element;

  const errorDetails = useMemo(
    () => ({
      hasError: !!value?.deleted,
      message: t('Please select a valid variable'),
    }),
    [value, t]
  );

  const isVisible = useOnScreen(componentRef);
  const validate = useFlashErrorDetails(errorDetails, isVisible);

  const variableValue = useMemo(() => addDeleted(value, t), [value, t]);

  return (
    <ElementInputWrapper className="help-me" ref={componentRef}>
      {element.argumentType === ELEMENT_TYPES.constant ? (
        <ValidatedDecimalNumberTextInput
          value={element.value}
          onChange={onUpdateElement}
        />
      ) : element.argumentType === ELEMENT_TYPES.field ? (
        <Select
          value={element.value}
          options={optionsForFields}
          label={null}
          placeholder={t('Choose Field')}
          onChange={onUpdateElement}
        />
      ) : (
        <GroupedVariableSelectWithAdd
          value={variableValue}
          options={optionsForVariables}
          label={null}
          placeholder={t('Choose Variable')}
          onChange={onUpdateElement}
          allowedDataTypes={ALLOWED_VARIABLE_DATA_TYPES}
          validate={validate}
          error={!!value?.deleted}
        />
      )}
    </ElementInputWrapper>
  );
};
